import React, { useState } from 'react';
import _sortBy from 'lodash/sortBy';
import _uniqBy from 'lodash/uniqBy';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';

import { buildCmpSubpagesUrls, promisesSettled } from 'utils';

import ZpFilter from '../../../components/zpFilter';
import {
  CAROUSEL_FILTERS,
  FILTER_PARAMS,
  FILTER_TYPES,
} from '../../../components/zpFilter/filterUtils';
import CommonService from '../../../services/commonService';
import { CMP_SUBPAGES,JOB_BUCKET_GROUP } from '../../../utils/constant';

import ChartTable from './chartTable';

const ReactTooltip = dynamic(() => import('react-tooltip'), { ssr: false });

const xLabels = [
  { female: '0%', marginF: 47, male: '100%', marginM: 31 },
  { female: '25%', marginF: 39, male: '75%', marginM: 39 },
  { female: '50%', marginF: 39, male: '50%', marginM: 39 },
  { female: '75%', marginF: 39, male: '25%', marginM: 39 },
  { female: '100%', marginF: 0, male: '0%', marginM: 0 },
];

const heads = [
  { title: 'Job Title', value: 'layTitle' },
  { title: 'Male', value: 'male' },
  { title: 'Female', value: 'female' },
];

const filterGroup = JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_GENDER_RATIO;

const GenderRatioComparision = ({
  jobTitle,
  genderRatio,
  compareGenderContentBlurb,
  filterValueRange,
  filterLogEventCallback,
  jobsIndex,
}) => {
  const [relatedTitles, setRelatedTitles] = useState(createRelatedTitles());

  function createRelatedTitles(newRelatedTitles = []) {
    const { relatedTitles, ...rest } = genderRatio;
    const relatedTitlesArray = relatedTitles && relatedTitles.length > 0 ? [...relatedTitles] : [];
    return _uniqBy(
      _sortBy(
        [
          { ...rest, layTitle: jobTitle, className: 'selected', isIndex: jobsIndex },
          ...newRelatedTitles,
          ...relatedTitlesArray,
        ],
        ['male']
      ),
      'layTitle'
    ).reverse();
  }

  const handleFilterUpdate = async (_, { value }) => {
    const genderRatioByTitle = value && value.originalSearch ? value.originalSearch : value;

    try {
      if (!genderRatioByTitle || !genderRatioByTitle.length) {
        setRelatedTitles(createRelatedTitles());
        return;
      }

      if (jobTitle === genderRatioByTitle) {
        return;
      }

      const data = {
        jobTitles: [genderRatioByTitle],
      };

      const promises = [
        CommonService.getTitlesIndexes(genderRatioByTitle),
        CommonService.fetchGenderRatioBarChartData(data),
      ];

      const [indexes, searchTitleInfo] = await promisesSettled(promises);

      const newRelatedTitles = searchTitleInfo.map((relatedTitle, idx) => ({
        ...relatedTitle,
        layTitle: data.jobTitles[idx],
        className: 'selected',
        isIndex: indexes?.[genderRatioByTitle].indexInfoOfJobsPage === 'INDEX',
      }));

      setRelatedTitles(createRelatedTitles(newRelatedTitles));
    } catch (error) {
      console.log(error);
    }
  };

  const filterProps = () => {
    return {
      ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
      buttonText: 'Compare Jobs',
      valueRange: filterValueRange,
      onApply: handleFilterUpdate,
      onClear: handleFilterUpdate,
      options: {
        logEvent: () => filterLogEventCallback(filterGroup),
      },
    };
  };

  return (
    <section id="gender-ratio-comparision" className="gender-ratio-comparision">
      <div className="section-separator-medium" />

      <h3 className="cmpSectionh4 mb-3">{`${jobTitle} Male to Female Ratio`}</h3>

      {compareGenderContentBlurb && compareGenderContentBlurb.content && (
        <div dangerouslySetInnerHTML={{ __html: compareGenderContentBlurb.content }} />
      )}

      <div className="mt-4">
        <ZpFilter {...filterProps()} />
      </div>

      <div className="d-block d-lg-none">
        <ChartTable
          heads={heads}
          tableData={relatedTitles}
          defaultSortedField="male"
          formatter={(value) => `${(value * 100).toFixed(0)}%`}
        />
      </div>

      <div className="d-none d-lg-block horizontal-chart">
        <div className="legends mb-2">
          <div className="openSansBlackBold legends-male">Male</div>
          <div className="openSansBlackBold legends-female">Female</div>
        </div>
        <div className="d-flex chart w-100">
          <div className="x-labels">
            {xLabels.map(({ male, marginM }, idx) => (
              <div key={idx} className="d-flex flex-column justify-content-end grid">
                <div className="grid-line" />
                <div className="lightTextStyle2 x-label" style={{ marginRight: marginM }}>
                  {male}
                </div>
              </div>
            ))}
          </div>
          <div className="y-labels my-3">
            {relatedTitles.map((relatedTitle, idx) => (
              <div key={v4()} className="d-flex align-items-center h-bar-container">
                <div className="y-label lightTextStyle2">
                  {relatedTitle.isIndex ? (
                    <a
                      className="alink"
                      href={buildCmpSubpagesUrls(relatedTitle.layTitle, '', CMP_SUBPAGES.JOBS)}
                    >
                      {relatedTitle.layTitle}
                    </a>
                  ) : (
                    <p>{relatedTitle.layTitle}</p>
                  )}
                </div>
                <div
                  className={`h-bar male ${relatedTitle.className ? relatedTitle.className : ''}`}
                  data-tip
                  data-for={`male-${relatedTitle.layTitle}-idx`}
                  style={{ width: 267 * relatedTitle.male, left: 277 - 267 * relatedTitle.male }}
                />
                <ReactTooltip id={`male-${relatedTitle.layTitle}-idx`} place="top">
                  <p className="mb-0 text-light">
                    {relatedTitle.layTitle}:{' '}
                    <strong>{(100 * relatedTitle.male).toFixed(0)}%</strong>
                  </p>
                </ReactTooltip>
                <div
                  className={`h-bar female ${relatedTitle.className ? relatedTitle.className : ''}`}
                  data-tip
                  data-for={`female-${relatedTitle.layTitle}-idx`}
                  style={{
                    width: 267 * relatedTitle.female,
                  }}
                />
                <ReactTooltip id={`female-${relatedTitle.layTitle}-idx`} place="top">
                  <p className="mb-0 text-light">
                    {relatedTitle.layTitle}:{' '}
                    <strong>{(100 * relatedTitle.female).toFixed(0)}%</strong>
                  </p>
                </ReactTooltip>
              </div>
            ))}
          </div>
          <div className="x-labels">
            {xLabels.map(({ female, marginF }, idx) => (
              <div key={idx} className="d-flex flex-column justify-content-end grid">
                <div className="grid-line" />
                <div className="lightTextStyle2 x-label" style={{ marginRight: marginF }}>
                  {female}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

GenderRatioComparision.propTypes = {
  jobTitle: PropTypes.string.isRequired,
  genderRatio: PropTypes.object.isRequired,
};

export default GenderRatioComparision;
